import React, { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, GlowRing, PromptCapsule, StarShape } from "./fx";

// HonestReturn — your bag has your items plus ONE glowing extra the seller
// gave you by mistake; return the glowing extra to the seller's crate. Trying
// to give back one of yours wiggles ("that one's yours — keep it!"). Faithful
// port of iOS HonestReturnActivityView.

const CRATE_UNIT = { x: 0.5, y: 0.24 };

const HonestReturn = ({ payload, sounds, onDone }) => {
  const yourCount = payload.yourCount || 3;
  const doneRef = useRef(false);
  const stageRef = useRef(null);
  const crateRef = useRef(null);

  const apples = useMemo(() => {
    const arr = Array.from({ length: yourCount }, (_, i) => ({ id: i, isExtra: false }));
    const at = Math.floor(Math.random() * (yourCount + 1));
    arr.splice(at, 0, { id: yourCount, isExtra: true });
    return arr;
  }, [yourCount]);

  const [dragging, setDragging] = useState(null); // {id, x, y}
  const [wiggle, setWiggle] = useState(null);
  const [returned, setReturned] = useState(false);
  const [burst, setBurst] = useState(0);

  const applePos = (index, count) => ({
    left: `${(0.5 + (index - (count - 1) / 2) * 0.16) * 100}%`,
    top: "72%",
  });

  const atCrate = useCallback((clientX, clientY) => {
    const r = crateRef.current?.getBoundingClientRect();
    if (!r) return false;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    return Math.hypot(clientX - cx, clientY - cy) < 110;
  }, []);

  const handleDown = useCallback((e, apple) => {
    if (returned) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = stageRef.current?.getBoundingClientRect();
    setDragging({ id: apple.id, isExtra: apple.isExtra, x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) });
  }, [returned]);

  const handleMove = useCallback((e) => {
    setDragging((d) => {
      if (!d) return d;
      const rect = stageRef.current?.getBoundingClientRect();
      return { ...d, x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0) };
    });
  }, []);

  const handleUp = useCallback(
    (e, apple) => {
      setDragging(null);
      if (!atCrate(e.clientX, e.clientY)) return;
      if (apple.isExtra) {
        sounds.playPop(0.9);
        sounds.playSparkle();
        setReturned(true);
        setBurst((b) => b + 1);
        if (!doneRef.current) {
          doneRef.current = true;
          window.setTimeout(() => onDone(), 1100);
        }
      } else {
        sounds.playThud();
        setWiggle(`${apple.id}-${Date.now()}`);
        sounds.speak("That one's yours — keep it! Give back the glowing apple.");
      }
    },
    [atCrate, onDone, sounds]
  );

  return (
    <div className="ch-activity-body ch-honest">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div className="ch-stage" ref={stageRef} style={{ touchAction: "none" }}>
        <div className="ch-honest-crate" style={{ left: `${CRATE_UNIT.x * 100}%`, top: `${CRATE_UNIT.y * 100}%` }}>
          <GlowRing hot={!!dragging} tint="#ffd45c" style={{ bottom: "-30px" }} />
          <motion.span
            ref={crateRef}
            className="ch-honest-crate-img"
            key={burst}
            animate={burst > 0 ? { scale: [1, 1.12, 1], rotate: [0, -3, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <Sprite name={payload.basketAsset || "ch_crate_apple"} />
          </motion.span>
          <SparkleBurst trigger={burst} reach={90} style={{ left: "50%", top: "50%" }} />
        </div>

        {apples.map((apple, i) => {
          if (returned && apple.isExtra) return null;
          const dragged = dragging?.id === apple.id;
          const pos = dragged
            ? { left: dragging.x, top: dragging.y }
            : applePos(i, apples.length);
          return (
            <motion.div
              key={apple.id}
              className={`ch-honest-apple ${apple.isExtra ? "is-extra" : ""} ${dragged ? "is-lifted" : ""}`}
              style={pos}
              onPointerDown={(e) => handleDown(e, apple)}
              onPointerMove={handleMove}
              onPointerUp={(e) => handleUp(e, apple)}
              role="button"
              tabIndex={-1}
              aria-label={apple.isExtra ? "The extra apple" : "Your apple"}
              animate={wiggle && wiggle.startsWith(`${apple.id}-`) ? { x: [0, -7, 7, -5, 5, 0] } : {}}
              transition={{ duration: 0.4 }}
            >
              {apple.isExtra && <span className="ch-honest-glow" />}
              <motion.span
                style={{ display: "block", width: "100%", height: "100%" }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 250, damping: 15, delay: 0.12 + i * 0.08 }}
              >
                <Sprite name={payload.itemAsset || "ch_grocery_apple"} />
              </motion.span>
              {apple.isExtra && (
                <span className="ch-honest-twinkle" aria-hidden="true">
                  <StarShape size={20} color="#ffd45c" />
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default HonestReturn;
